import { walletMessageFor, WALLET_MESSAGE_VARS } from "./walletPresentation";
import { changeReminderType, reminderVariables } from "./reminders";
import TmdbSettings, { TmdbToggle } from "./TmdbSettings";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useDialogStyles } from "../../theme/redesign";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  defaultSymbol,
  DEFAULT_REMINDER_MESSAGE,
  fetchExchangeRate,
  LATAM_CURRENCIES,
  money,
  NEGOCIO_MESSAGE_VARS,
  setNegocioFormat,
} from "./helpers";
import { DialogClose, NegocioPager, PageFrame, RecordsState, SearchField, StatusChip, stopRow, tNegocio, usePager } from "./shared";
import { DEFAULT_PLAN_CATALOG, parsePlanCatalog, productOfferLabel, productTypeLabel } from "./catalog";
import NegocioMessageEditor from "./MessageBox";
import { ProductMark } from "./AccountPeek";
import { useConfirm, useRecords } from "./Lists";
import { useStyles } from "./styles";
import CodeLookupSettings from "./CodeLookupSettings";
import BinancePaySettings from "./BinancePaySettings";
import MigrateAppSheetTab from "./MigrateAppSheetTab";
import AiAgentTab from "./AiAgentTab";
import GrowthSection from "./GrowthSection";
import StorefrontSettings from "./StorefrontSettings";
import {
  RecordCard,
  RecordCardMeta,
  RecordsCardsGrid,
  useViewMode,
  ViewModeToggle,
} from "../../components/ViewModeToggle";

const SETTINGS_HINT = {
  planes: "pages.settings.plansHint",
  pagos: "pages.settings.paymentsUnifiedHint",
  mensajes: "pages.settings.messagesUnifiedHint",
  uso: "pages.settings.termsHint",
  fidelizacion: "pages.settings.loyaltyHint",
  codigos: "pages.settings.codesHint",
  bot: "pages.settings.botHint",
  "agente-ia": "pages.ai.hint",
  migrar: "pages.settings.migrateHint",
};

const TABS = [
  { id: "pagos", titleKey: "pages.settings.tabs.payments" },
  { id: "planes", titleKey: "pages.settings.tabs.plans" },
  { id: "mensajes", titleKey: "pages.settings.tabs.messages" },
  { id: "uso", titleKey: "pages.settings.tabs.terms" },
  { id: "fidelizacion", titleKey: "pages.settings.tabs.loyalty" },
  { id: "codigos", titleKey: "pages.settings.tabs.codes" },
  { id: "tienda", title: "Tienda web" },
  { id: "bot", titleKey: "pages.settings.tabs.bot" },
  { id: "agente-ia", titleKey: "pages.ai.tab" },
  { id: "integraciones", title: "Integraciones" },
  { id: "migrar", titleKey: "pages.settings.tabs.migrate" },
];

const TAB_ALIASES = {
  moneda: "pagos",
  recordatorios: "mensajes",
  sorteo: "fidelizacion",
  puntos: "fidelizacion",
  fidelización: "fidelizacion",
};

const uploadMedia = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/files/upload-single", formData);
  return data.path;
};

const emptyPayment = () => ({
  name: "",
  type: "",
  accountNumber: "",
  reference: "",
  identityNumber: "",
  notes: "",
  isActive: true,
});

const emptyReminder = () => ({
  reminderType: "expiration",
  name: "",
  message: DEFAULT_REMINDER_MESSAGE,
  includePointsMessage: false,
  extraMessage: "",
  imageUrl: "",
  daysOffset: 3,
  frequency: "daily",
  sendHour: "09:00",
  notifyNumber: "",
  isActive: true,
});

function PlansTab({ settings, setSettings, onSave }) {
  const classes = useStyles();
  const plans = parsePlanCatalog(settings?.planCatalog);
  const [draft, setDraft] = useState("");

  const persist = (next) => {
    const clean = parsePlanCatalog(next);
    setSettings((s) => ({ ...s, planCatalog: clean }));
    onSave({ planCatalog: clean });
  };

  const add = () => {
    const name = String(draft || "").trim();
    if (!name) return;
    if (plans.some((item) => item.toLowerCase() === name.toLowerCase())) {
      setDraft("");
      return;
    }
    persist([...plans, name]);
    setDraft("");
  };

  const remove = (name) => persist(plans.filter((item) => item !== name));

  const reset = () => persist([...DEFAULT_PLAN_CATALOG]);

  return (
    <>
      <div className={classes.captionBlock} style={{ marginBottom: 12 }}>
        {tNegocio("pages.settings.plansHint")}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
        <TextField
          size="small"
          variant="outlined"
          label={tNegocio("pages.settings.planName")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          style={{ minWidth: 220 }}
        />
        <Button color="primary" variant="contained" onClick={add}>
          {tNegocio("pages.settings.addPlan")}
        </Button>
        <Button onClick={reset}>{tNegocio("pages.settings.resetPlans")}</Button>
      </div>
      {plans.length ? (
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tNegocio("fields.planLabel")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((name) => (
                <TableRow key={name}>
                  <TableCell style={{ fontWeight: 700 }}>{name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => remove(name)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className={classes.captionBlock}>{tNegocio("pages.settings.noPlans")}</div>
      )}
    </>
  );
}

function currencyLabel(item) {
  return `${item.code} · ${item.name}`;
}

function CurrencyTab({ settings, setSettings, onSave }) {
  const classes = useStyles();
  const localCode = settings.currencyLocal || "COP";
  const fxCode = settings.currencyFx || "USD";
  const symbol = settings.currencySymbol || defaultSymbol(localCode);
  const roundRate = (value) => localCode === "COP" ? Math.round(Number(value)) : Number(Number(value).toFixed(6));
  const rate = Number(settings.exchangeRate);
  const rateInvalid = !Number.isFinite(rate) || rate <= 0;
  const [fxState, setFxState] = useState({ loading: false, error: "", updatedAt: "", source: "", cached: false });

  useEffect(() => {
    setNegocioFormat({ currency: localCode, symbol });
  }, [localCode, symbol]);

  const consultRate = async () => {
    setFxState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const result = await fetchExchangeRate(fxCode, localCode, { force: true });
      const nextRate = roundRate(result.rate);
      setSettings((s) => ({ ...s, exchangeRate: nextRate }));
      setFxState({
        loading: false,
        error: "",
        updatedAt: result.updatedAt,
        source: result.source,
        cached: !!result.cached,
      });
    } catch (e) {
      setFxState({
        loading: false,
        error: tNegocio("pages.settings.rateError"),
        updatedAt: "",
        source: "",
        cached: false,
      });
    }
  };

  return (
    <>
      <div className={classes.settingsGrid}>
        <TextField
          select
          size="small"
          variant="outlined"
          label={tNegocio("pages.settings.currencyLocal")}
          value={LATAM_CURRENCIES.some((c) => c.code === localCode) ? localCode : "COP"}
          onChange={(e) => {
            const code = e.target.value;
            setSettings((s) => ({
              ...s,
              currencyLocal: code,
              currencySymbol:
                !s.currencySymbol || s.currencySymbol === defaultSymbol(s.currencyLocal)
                  ? defaultSymbol(code)
                  : s.currencySymbol,
            }));
          }}
        >
          {LATAM_CURRENCIES.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              {currencyLabel(c)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          label={tNegocio("pages.settings.currencySymbol")}
          value={symbol}
          onChange={(e) => setSettings((s) => ({ ...s, currencySymbol: e.target.value.slice(0, 8) }))}
          inputProps={{ maxLength: 8 }}
        />
        <TextField
          select
          size="small"
          variant="outlined"
          label={tNegocio("pages.settings.currencyFx")}
          value={LATAM_CURRENCIES.some((c) => c.code === fxCode) ? fxCode : "USD"}
          onChange={(e) => setSettings((s) => ({ ...s, currencyFx: e.target.value }))}
        >
          {LATAM_CURRENCIES.map((c) => (
            <MenuItem key={`fx-${c.code}`} value={c.code}>
              {currencyLabel(c)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          type="number"
          label={tNegocio("pages.settings.exchangeRate")}
          error={rateInvalid}
          helperText={rateInvalid ? tNegocio("pages.settings.rateInvalid") : undefined}
          value={settings.exchangeRate === "" ? "" : localCode === "COP" ? roundRate(settings.exchangeRate) : settings.exchangeRate ?? ""}
          disabled={fxState.loading}
          onChange={(e) => setSettings((s) => ({ ...s, exchangeRate: e.target.value }))}
          onBlur={() => {
            if (!rateInvalid) setSettings((s) => ({ ...s, exchangeRate: roundRate(s.exchangeRate) }));
          }}
          inputProps={{ min: localCode === "COP" ? 1 : 0.000001, step: localCode === "COP" ? "1" : "any" }}
        />
        <TextField
          size="small"
          variant="outlined"
          type="number"
          label={tNegocio("pages.settings.profitPercent")}
          value={settings.profitPercent != null && settings.profitPercent !== "" ? settings.profitPercent : 50}
          onChange={(e) => setSettings((s) => ({ ...s, profitPercent: e.target.value }))}
          inputProps={{ min: 0, step: "1" }}
          helperText={tNegocio("pages.settings.profitPercentHint")}
        />
        <TextField
          size="small"
          variant="outlined"
          type="number"
          label={tNegocio("pages.settings.binanceTtlHours")}
          value={settings.binanceCheckoutTtlHours != null && settings.binanceCheckoutTtlHours !== "" ? settings.binanceCheckoutTtlHours : 72}
          onChange={(e) => setSettings((s) => ({ ...s, binanceCheckoutTtlHours: e.target.value }))}
          inputProps={{ min: 6, max: 168, step: "1" }}
          helperText={tNegocio("pages.settings.binanceTtlHoursHint")}
        />
      </div>
      <div className={classes.captionBlock} style={{ marginTop: 12 }}>
        {tNegocio("pages.settings.rateDaily")}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
        <Button size="small" variant="outlined" color="primary" onClick={consultRate} disabled={fxState.loading}>
          {fxState.loading ? tNegocio("pages.settings.fetchingRate") : tNegocio("pages.settings.fetchRate")}
        </Button>
        <Button size="small" variant="contained" color="primary" onClick={() => onSave({ exchangeRate: roundRate(settings.exchangeRate) })} disabled={fxState.loading || rateInvalid || roundRate(rate) <= 0}>
          {tNegocio("save")}
        </Button>
        {fxState.error ? (
          <span className={classes.captionBlock}>{fxState.error}</span>
        ) : fxState.updatedAt ? (
          <span className={classes.captionBlock}>{`1 ${fxCode} = ${roundRate(rate)} ${localCode} · ${fxState.source} · ${fxState.updatedAt}`}</span>
        ) : null}
      </div>
    </>
  );
}

function PaymentsTab() {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const { records, loading, reload } = useRecords("payment-methods");
  const { confirm, setConfirm, runConfirm } = useConfirm(reload);
  const [viewMode, setViewMode] = useViewMode("negocio-payments");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(null);
  const rows = useMemo(
    () => records.filter((p) => matchesPayment(p, query)),
    [records, query]
  );
  const pager = usePager(rows);

  const save = async () => {
    if (!form) return;
    try {
      if (form.id) await api.put(`/negocio/payment-methods/${form.id}`, form);
      else await api.post("/negocio/payment-methods", form);
      toast.success(tNegocio("toastSaved"));
      setForm(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  const renderActions = (p) => (
    <div className={classes.actionCluster}>
      <IconButton size="small" onClick={stopRow(() => setForm({ ...emptyPayment(), ...p }))}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={stopRow(() => setConfirm({ action: "delete", resource: "payment-methods", id: p.id }))}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </div>
  );

  return (
    <>
      <div className={classes.toolbar}>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
          <SearchField value={query} onChange={setQuery} />
          <Button variant="contained" color="primary" onClick={() => setForm(emptyPayment())}>
            {tNegocio("new.payment")}
          </Button>
        </div>
      </div>
      <RecordsState
        loading={loading}
        count={rows.length}
        emptyTitle={tNegocio("pages.payments.emptyTitle")}
        emptySubtitle={tNegocio("pages.payments.empty")}
      >
        {viewMode === "cards" ? (
        <RecordsCardsGrid>
          {pager.slice.map((p) => (
            <RecordCard key={p.id} onClick={() => setForm({ ...emptyPayment(), ...p })} actions={renderActions(p)}>
              <div style={{ fontWeight: 800 }}>{p.name}</div>
              <RecordCardMeta>
                {p.type || tNegocio("fields.accountType")}
                {p.accountNumber ? <div>{p.accountNumber}</div> : null}
                {p.reference ? <div>{tNegocio("fields.reference")}: {p.reference}</div> : null}
                {p.identityNumber ? <div>{tNegocio("fields.identityNumber")}: {p.identityNumber}</div> : null}
              </RecordCardMeta>
              <StatusChip value={p.isActive === false ? "disabled" : "active"} />
            </RecordCard>
          ))}
        </RecordsCardsGrid>
        ) : (
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tNegocio("fields.name")}</TableCell>
                <TableCell>{tNegocio("fields.accountType")}</TableCell>
                <TableCell>{tNegocio("fields.accountNumber")}</TableCell>
                <TableCell>{tNegocio("fields.reference")}</TableCell>
                <TableCell>{tNegocio("fields.identityNumber")}</TableCell>
                <TableCell>{tNegocio("cols.status")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pager.slice.map((p) => (
                <TableRow key={p.id} hover className={classes.clickableRow} onClick={() => setForm({ ...emptyPayment(), ...p })}>
                  <TableCell>
                    <div style={{ fontWeight: 700 }}>{p.name}</div>
                    {p.notes ? <div className={classes.captionBlock}>{p.notes}</div> : null}
                  </TableCell>
                  <TableCell>{p.type || "—"}</TableCell>
                  <TableCell>{p.accountNumber || "—"}</TableCell>
                  <TableCell>{p.reference || "—"}</TableCell>
                  <TableCell>{p.identityNumber || "—"}</TableCell>
                  <TableCell>
                    <StatusChip value={p.isActive === false ? "disabled" : "active"} />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => setForm({ ...emptyPayment(), ...p })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setConfirm({ action: "delete", resource: "payment-methods", id: p.id })}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
        </div>
        )}
        {viewMode === "cards" ? (
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
        ) : null}
      </RecordsState>
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setForm(null)} />
        <DialogTitle>{form?.id ? tNegocio("edit") : tNegocio("new.payment")}</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth margin="dense" variant="outlined" label={tNegocio("fields.name")} value={form?.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField fullWidth margin="dense" variant="outlined" label={tNegocio("fields.accountType")} value={form?.type || ""} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} placeholder="Ahorros, Nequi, USDT…" />
          <TextField fullWidth margin="dense" variant="outlined" label={tNegocio("fields.accountNumber")} value={form?.accountNumber || ""} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} />
          <TextField fullWidth margin="dense" variant="outlined" label={tNegocio("fields.reference")} value={form?.reference || ""} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
          <TextField fullWidth margin="dense" variant="outlined" label={tNegocio("fields.identityNumber")} value={form?.identityNumber || ""} onChange={(e) => setForm((f) => ({ ...f, identityNumber: e.target.value }))} />
          <TextField fullWidth margin="dense" variant="outlined" multiline rows={2} label={tNegocio("fields.comment")} value={form?.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <FormControlLabel
            control={<Switch checked={form?.isActive !== false} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} color="primary" />}
            label={form?.isActive === false ? tNegocio("status.disabled") : tNegocio("status.active")}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={save}>{tNegocio("save")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal title={tNegocio("confirmTitle")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={runConfirm}>
        {tNegocio("confirmBody")}
      </ConfirmationModal>
    </>
  );
}

const matchesPayment = (p, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  return [p.name, p.type, p.accountNumber, p.reference, p.identityNumber, p.notes].some((v) =>
    String(v || "").toLowerCase().includes(q)
  );
};

function MessagesTab({ settings, setSettings, onSaveSettings }) {
  const classes = useStyles();
  const products = useRecords("products");
  const [openId, setOpenId] = useState("general");
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    const next = {};
    (products.records || []).forEach((p) => {
      next[p.id] = {
        saleMessage: p.saleMessage || "",
        renewMessage: p.renewMessage || "",
        expireMessage: p.expireMessage || "",
      };
    });
    setDrafts(next);
  }, [products.records]);

  const saveProduct = async (product) => {
    try {
      await api.put(`/negocio/products/${product.id}`, { ...(drafts[product.id] || {}) });
      toast.success(tNegocio("toastSaved"));
      products.reload();
    } catch (e) {
      toastError(e);
    }
  };

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const block = (title, kind, source, setter) => (
    <NegocioMessageEditor
      label={title}
      value={source[kind] || ""}
      onChange={(v) => setter({ [kind]: v })}
      placeholder={tNegocio(`pages.settings.${kind}Placeholder`)}
    />
  );

  return (
    <>
      <div className={classes.accordionCard}>
        <div className={classes.productHead} style={{ padding: 12, cursor: "pointer" }} onClick={() => setOpenId(openId === "general" ? "" : "general")} role="button" tabIndex={0}>
          <div style={{ flex: 1, fontWeight: 800 }}>{tNegocio("pages.settings.generalMessages")}</div>
          <IconButton size="small">{openId === "general" ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </div>
        <Collapse in={openId === "general"} unmountOnExit>
          <div style={{ padding: 16 }}>
            {block(tNegocio("pages.settings.saleMessage"), "saleMessage", settings, (patch) => setSettings((s) => ({ ...s, ...patch })))}
            {block(tNegocio("pages.settings.renewMessage"), "renewMessage", settings, (patch) => setSettings((s) => ({ ...s, ...patch })))}
            {block(tNegocio("pages.settings.expireMessage"), "expireMessage", settings, (patch) => setSettings((s) => ({ ...s, ...patch })))}
            {block(tNegocio("pages.settings.updateMessage"), "updateMessage", settings, (patch) => setSettings((s) => ({ ...s, ...patch })))}
            <NegocioMessageEditor
              label={tNegocio("pages.settings.payMessage")}
              value={settings.payMessage || ""}
              onChange={(v) => setSettings((s) => ({ ...s, payMessage: v }))}
              placeholder={tNegocio("pages.settings.payMessagePlaceholder")}
              varChips={[
                ...NEGOCIO_MESSAGE_VARS.filter((v) =>
                  ["nombre_servicio", "nombre_cliente", "precio"].includes(v.key)
                ),
                { key: "usdt", label: "USDT exacto", sample: "2.00", color: "#f0b90b" },
                { key: "pay_id", label: "Pay ID", sample: "294603789", color: "#f0b90b" },
                { key: "vence_pago", label: "Vence pago", sample: "25/09/2026 18:00", color: "#ed6c02" },
              ]}
            />
            <NegocioMessageEditor
              label={tNegocio("pages.settings.pointsMessage")}
              value={walletMessageFor(settings.pointsMessage)}
              onChange={(v) => setSettings((s) => ({ ...s, pointsMessage: v }))}
              varChips={WALLET_MESSAGE_VARS}
            />
            <div className={classes.captionBlock}>{tNegocio("pages.settings.pointsMessageHint")}</div>
            {settings.pointsSystemEnabled !== false && settings.pointsSystemEnabled !== 0 ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={settings.pointsEarnedEnabled !== false && settings.pointsEarnedEnabled !== 0}
                      onChange={(e) => setSettings((s) => ({ ...s, pointsEarnedEnabled: e.target.checked }))}
                    />
                  }
                  label={tNegocio("pages.settings.pointsEarnedEnabled")}
                />
                {settings.pointsEarnedEnabled !== false && settings.pointsEarnedEnabled !== 0 ? (
                  <>
                    <NegocioMessageEditor
                      label={tNegocio("pages.settings.pointsEarnedMessage")}
                      value={settings.pointsEarnedMessage || ""}
                      onChange={(v) => setSettings((s) => ({ ...s, pointsEarnedMessage: v }))}
                      placeholder={tNegocio("pages.settings.pointsEarnedMessagePlaceholder")}
                      varChips={POINTS_EARNED_CHIPS}
                      rows={9}
                    />
                    <div className={classes.captionBlock} style={{ margin: "-4px 0 12px" }}>
                      {tNegocio("pages.settings.pointsEarnedHint")}
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            <Button variant="contained" color="primary" onClick={() => onSaveSettings()} style={{ marginTop: 12 }}>
              {tNegocio("pages.settings.saveTemplates")}
            </Button>
          </div>
        </Collapse>
      </div>
      {(products.records || [])
        .slice()
        .sort((a, b) =>
          String(productOfferLabel(a, tNegocio, { days: false })).localeCompare(
            String(productOfferLabel(b, tNegocio, { days: false })),
            undefined,
            { sensitivity: "base" }
          )
        )
        .map((p) => {
        const open = openId === p.id;
        const draft = drafts[p.id] || {};
        const meta = [
          productTypeLabel(p, tNegocio),
          p.planLabel || null,
          p.durationDays ? `${p.durationDays}d` : null,
          `#${p.id}`,
          p.isActive === false ? tNegocio("status.disabled") : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <div key={p.id} className={classes.accordionCard}>
            <div className={classes.productHead} style={{ padding: 12, cursor: "pointer", alignItems: "center" }} onClick={() => setOpenId(open ? "" : p.id)} role="button" tabIndex={0}>
              <ProductMark product={p} size={36} circle link={false} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800 }}>{productOfferLabel(p, tNegocio, { days: false })}</div>
                <div className={classes.captionBlock} style={{ marginTop: 2 }}>
                  {meta}
                </div>
              </div>
              <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
            </div>
            <Collapse in={open} unmountOnExit>
              <div style={{ padding: 16 }}>
                {block(tNegocio("pages.settings.saleMessage"), "saleMessage", draft, (patch) => setDraft(p.id, patch))}
                {block(tNegocio("pages.settings.renewMessage"), "renewMessage", draft, (patch) => setDraft(p.id, patch))}
                {block(tNegocio("pages.settings.expireMessage"), "expireMessage", draft, (patch) => setDraft(p.id, patch))}
                <Button variant="contained" color="primary" onClick={() => saveProduct(p)} style={{ marginTop: 12 }}>
                  {tNegocio("pages.settings.saveTemplates")}
                </Button>
              </div>
            </Collapse>
          </div>
        );
      })}
    </>
  );
}

function RemindersTab() {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const { records, loading, reload } = useRecords("reminders");
  const { confirm, setConfirm, runConfirm } = useConfirm(reload);
  const [form, setForm] = useState(null);
  const pager = usePager(records);
  const payment = form?.reminderType === "pending_payment";
  const chips = reminderVariables(payment);

  const save = async () => {
    if (!form) return;
    try {
      let imageUrl = form.imageUrl || null;
      if (form.imageFile) imageUrl = await uploadMedia(form.imageFile);
      const payload = {
        reminderType: form.reminderType || "expiration",
        name: form.name,
        message: form.message,
        includePointsMessage: !!form.includePointsMessage,
        extraMessage: form.extraMessage || "",
        imageUrl,
        daysOffset: form.daysOffset,
        frequency: form.frequency || "daily",
        sendHour: form.sendHour || "09:00",
        notifyNumber: form.notifyNumber,
        isActive: form.isActive !== false,
      };
      if (form.id) await api.put(`/negocio/reminders/${form.id}`, payload);
      else await api.post("/negocio/reminders", payload);
      toast.success(tNegocio("toastSaved"));
      setForm(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  const daysLabel = (n) => {
    const days = Number(n);
    if (days > 0) return tNegocio("pages.settings.daysBefore").replace("{{n}}", String(days));
    if (days < 0) return tNegocio("pages.settings.daysAfter").replace("{{n}}", String(Math.abs(days)));
    return tNegocio("pages.settings.daysSame");
  };

  return (
    <>
      <div className={classes.toolbar}>
        <Button variant="contained" color="primary" onClick={() => setForm(emptyReminder())}>
          {tNegocio("pages.settings.newReminder")}
        </Button>
      </div>
      <RecordsState
        loading={loading}
        count={records.length}
        emptyTitle={tNegocio("pages.settings.noReminders")}
        emptySubtitle={tNegocio("pages.settings.noRemindersHint")}
      >
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tNegocio("cols.name")}</TableCell>
                <TableCell>{tNegocio("pages.settings.reminderType")}</TableCell>
                <TableCell>{tNegocio("pages.settings.when")}</TableCell>
                <TableCell>{tNegocio("pages.settings.frequency")}</TableCell>
                <TableCell>{tNegocio("pages.settings.sendHour")}</TableCell>
                <TableCell>{tNegocio("pages.settings.notifyNumber")}</TableCell>
                <TableCell>{tNegocio("cols.status")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pager.slice.map((r) => (
                <TableRow key={r.id} hover className={classes.clickableRow} onClick={() => setForm({ ...emptyReminder(), ...r })}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{tNegocio(r.reminderType === "pending_payment" ? "pages.settings.reminderPayment" : "pages.settings.reminderExpiration")}</TableCell>
                  <TableCell>{r.reminderType === "pending_payment" ? tNegocio("pages.settings.paymentDaysSummary").replace("{{n}}", String(r.daysOffset)) : daysLabel(r.daysOffset)}</TableCell>
                  <TableCell>
                    {r.frequency === "once"
                      ? tNegocio(r.reminderType === "pending_payment" ? "pages.settings.paymentFrequencyOnce" : "pages.settings.frequencyOnce")
                      : tNegocio("pages.settings.frequencyDaily")}
                  </TableCell>
                  <TableCell>{r.sendHour || "09:00"}</TableCell>
                  <TableCell>{r.notifyNumber || "—"}</TableCell>
                  <TableCell>
                    <StatusChip value={r.isActive === false ? "disabled" : "active"} />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => setForm({ ...emptyReminder(), ...r })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setConfirm({ action: "delete", resource: "reminders", id: r.id })}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
        </div>
      </RecordsState>
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="md" fullWidth classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setForm(null)} />
        <DialogTitle>{form?.id ? tNegocio("edit") : tNegocio("pages.settings.newReminder")}</DialogTitle>
        <DialogContent dividers>
          <TextField select fullWidth margin="dense" variant="outlined"
            label={tNegocio("pages.settings.reminderType")}
            helperText={tNegocio(payment ? "pages.settings.reminderPaymentHint" : "pages.settings.reminderExpirationHint")}
            value={form?.reminderType || "expiration"}
            onChange={(e) => { const type = e.target.value; setForm((f) => changeReminderType(f, type)); }}>
            <MenuItem value="expiration">{tNegocio("pages.settings.reminderExpiration")}</MenuItem>
            <MenuItem value="pending_payment">{tNegocio("pages.settings.reminderPayment")}</MenuItem>
          </TextField>
          <TextField fullWidth margin="dense" variant="outlined" label={tNegocio("fields.name")} value={form?.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio(payment ? "pages.settings.paymentDays" : "pages.settings.daysOffset")}
            helperText={tNegocio(payment ? "pages.settings.paymentDaysHint" : "pages.settings.daysOffsetHint")}
            inputProps={payment ? { min: 0, step: 1 } : { step: 1 }}
            value={form?.daysOffset ?? 3}
            onChange={(e) => setForm((f) => ({ ...f, daysOffset: e.target.value }))}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.settings.frequency")}
            helperText={tNegocio(payment ? "pages.settings.paymentFrequencyHint" : "pages.settings.frequencyHint")}
            value={form?.frequency || "daily"}
            onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
          >
            <MenuItem value="daily">{tNegocio("pages.settings.frequencyDaily")}</MenuItem>
            <MenuItem value="once">{tNegocio(payment ? "pages.settings.paymentFrequencyOnce" : "pages.settings.frequencyOnce")}</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="time"
            label={tNegocio("pages.settings.sendHour")}
            helperText={tNegocio("pages.settings.sendHourHint")}
            InputLabelProps={{ shrink: true }}
            value={form?.sendHour || "09:00"}
            onChange={(e) => setForm((f) => ({ ...f, sendHour: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.settings.notifyNumber")}
            value={form?.notifyNumber || ""}
            onChange={(e) => setForm((f) => ({ ...f, notifyNumber: e.target.value }))}
          />
          <NegocioMessageEditor
            label={tNegocio("pages.settings.reminderMessage")}
            varChips={chips}
            value={form?.message || ""}
            onChange={(v) => setForm((f) => ({ ...f, message: v }))}
            showAttach
            mediaUrl={form?.preview || form?.imageUrl}
            onMedia={(file) =>
              setForm((f) => ({
                ...f,
                imageFile: file || null,
                imageUrl: file ? f.imageUrl : "",
                preview: file ? URL.createObjectURL(file) : "",
              }))
            }
          />
          <NegocioMessageEditor
            label={tNegocio("pages.settings.reminderExtraMessage")}
            varChips={chips}
            value={form?.extraMessage || ""}
            onChange={(v) => setForm((f) => ({ ...f, extraMessage: v }))}
          />
          <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
            {tNegocio("pages.settings.reminderExtraMessageHint")}
          </div>
          {!payment ? <>
            <FormControlLabel
              control={<Switch checked={!!form?.includePointsMessage} onChange={(e) => setForm((f) => ({ ...f, includePointsMessage: e.target.checked }))} color="primary" />}
              label={tNegocio("pages.settings.reminderWalletMessage")}
            />
            <div className={classes.captionBlock}>{tNegocio("pages.settings.reminderWalletMessageHint")}</div>
          </> : null}
          <FormControlLabel
            control={<Switch checked={form?.isActive !== false} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} color="primary" />}
            label={form?.isActive === false ? tNegocio("status.disabled") : tNegocio("status.active")}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={save}>{tNegocio("save")}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal title={tNegocio("confirmTitle")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={runConfirm}>
        {tNegocio("confirmBody")}
      </ConfirmationModal>
    </>
  );
}

const POINTS_EARNED_CHIPS = [
  { key: "nombre_cliente", label: "Cliente", color: "#00695c" },
  { key: "saldo_ganado", label: "Saldo ganado", color: "#16a34a" },
  { key: "saldo", label: "Saldo disponible", color: "#f59e0b" },
  { key: "servicios_compra", label: "Servicios comprados", color: "#0284c7" },
  { key: "valor_compra", label: "Valor compra", color: "#4527a0" },
  { key: "codigo", label: "Código cliente", color: "#546e7a" },
];

const RAFFLE_MSG_CHIPS = [
  { key: "nombre_cliente", label: "Cliente", color: "#00695c" },
  { key: "nombre_ganador", label: "Ganador", color: "#ea580c" },
  { key: "mes", label: "Mes", color: "#0284c7" },
  { key: "tickets_ganador", label: "Tickets ganador", color: "#7c3aed" },
  { key: "total_tickets", label: "Tickets totales", color: "#0d9488" },
  { key: "participantes", label: "Participantes", color: "#546e7a" },
  { key: "premio_vence", label: "Vence premio", color: "#c62828" },
  { key: "telefono_ganador", label: "Tel. ganador", color: "#1565c0" },
];

const CONDITION_CHIPS = {
  uso: [
    { key: "vigencia", label: "Vigencia", color: "#0284c7", text: "\n*Vigencia:* el acceso se entrega por el periodo contratado y se corta al vencer si no hay renovación.\n" },
    { key: "uso", label: "Uso permitido", color: "#0d9488", text: "\n*Uso permitido:* el servicio es personal. No se puede revender ni compartir fuera de lo acordado.\n" },
    { key: "soporte", label: "Soporte", color: "#7c3aed", text: "\n*Soporte:* para fallas o cambios, escribe por WhatsApp al mismo número de atención.\n" },
    { key: "reembolso", label: "Reembolsos", color: "#db2777", text: "\n*Reembolsos:* no aplican una vez entregado el acceso, salvo falla demostrable del servicio.\n" },
  ],
  sorteo: [
    { key: "fecha", label: "Fecha", color: "#0284c7", text: "\n*Fecha:* el sorteo se realiza automáticamente el día 1 de cada mes, con las compras y renovaciones del mes anterior.\n" },
    { key: "premio", label: "Premio", color: "#ea580c", text: "\n*Premio:* 30 días de un servicio (renovar o uno nuevo), sujeto a stock. No es transferible ni canjeable por dinero.\n" },
    { key: "tickets", label: "Tickets", color: "#0d9488", text: "\n*Oportunidades:* cada compra o renovación suma un ticket. Entre más servicios actives, más chances tienes.\n" },
    { key: "canje", label: "Canje", color: "#7c3aed", text: "\n*Canje:* el ganador debe tener al menos un servicio activo y vigente. El premio vence a los 30 días.\n" },
  ],
  puntos: [
    { key: "ganar", label: "Cómo ganar", color: "#0284c7", text: "\n*Cómo ganar:* cada compra o renovación pagada con dinero te devuelve un % de su valor como saldo en tu monedero.\n" },
    { key: "canje", label: "Cómo usarlo", color: "#0d9488", text: "\n*Cómo usarlo:* paga compras nuevas o renovaciones con la opción \"💳 Pagar con saldo\". El saldo debe cubrir el valor completo.\n" },
    { key: "caducidad", label: "Vigencia", color: "#ea580c", text: "\n*Vigencia:* el saldo se mantiene mientras tengas al menos un servicio activo.\n" },
    { key: "exclusiones", label: "Exclusiones", color: "#db2777", text: "\n*Exclusiones:* pagos pendientes o cancelados y lo pagado con saldo no generan cashback.\n" },
  ],
};

const cashbackTermsTemplate = (settings) => {
  const percent = Number(settings.cashbackPercent || 0);
  const local = String(settings.currencyLocal || "COP").toUpperCase();
  const fx = String(settings.currencyFx || "USD").toUpperCase();
  return [
    "💰 SALDO Y CASHBACK 💰",
    "",
    "1️⃣ ¿Cómo gano saldo?",
    `Por cada compra o renovación pagada recibes el ${percent}% de su valor como saldo en tu monedero. Se abona cuando se confirma el pago.`,
    "",
    "2️⃣ Moneda del saldo",
    `Tu saldo se maneja en la moneda de tu cuenta: ${local} si eres cliente local o ${fx} si estás en otro país.`,
    "",
    "3️⃣ ¿Cómo lo uso?",
    "Paga compras nuevas o renovaciones con la opción \"💳 Pagar con saldo\". El saldo debe cubrir el valor completo del servicio; no se combinan pagos parciales de saldo y dinero.",
    "",
    "4️⃣ ¿Qué no genera cashback?",
    "Lo pagado con saldo, los pagos pendientes y los cancelados no generan saldo.",
    "",
    "5️⃣ Recargas y consulta",
    "Puedes recargar tu monedero y consultar tu saldo cuando quieras escribiendo SALDO.",
    "",
    "6️⃣ Condiciones",
    "El saldo es personal e intransferible y no se retira ni se devuelve en dinero. Las condiciones pueden cambiar, siempre con aviso.",
    "",
    "7️⃣ Vigencia del saldo",
    "Tu saldo solo se puede usar mientras tengas al menos un servicio en tu carrito, aunque esté vencido (puedes renovarlo con saldo). Si no renuevas a tiempo y el servicio se suspende y se retira, quedas sin servicios y el saldo se pierde (queda en 0).",
    "",
    "🔥 Entre más compras y renuevas, más saldo ganas para tus próximos servicios.",
  ].join("\n");
};

function ConditionsTab({ settings, setSettings, onSave, textKey, mediaKey, placeholder, chips, children }) {
  const onMedia = async (file) => {
    if (!file) {
      setSettings((s) => ({ ...s, [mediaKey]: "" }));
      return;
    }
    try {
      const path = await uploadMedia(file);
      setSettings((s) => ({ ...s, [mediaKey]: path }));
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <>
      {children}
      <NegocioMessageEditor
        value={settings[textKey] || ""}
        onChange={(v) => setSettings((s) => ({ ...s, [textKey]: v }))}
        showAttach
        mediaUrl={settings[mediaKey]}
        onMedia={onMedia}
        placeholder={placeholder || tNegocio("pages.settings.termsPlaceholder")}
        varChips={chips}
        rows={6}
        attachAccept="image/*,application/pdf"
      />
      <Button variant="contained" color="primary" onClick={() => onSave()} style={{ marginTop: 12 }}>
        {tNegocio("save")}
      </Button>
    </>
  );
}

function BotTab({ settings, setSettings, onSave }) {
  const classes = useStyles();
  const [queues, setQueues] = useState([]);
  const [tokenDraft, setTokenDraft] = useState("");
  const [testingBot, setTestingBot] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(Array.isArray(data) ? data : data?.queues || []);
      } catch (e) {
        toastError(e);
      }
    })();
  }, []);

  useEffect(() => {
    setTokenDraft(settings.botTelegramToken === "********" ? "" : settings.botTelegramToken || "");
  }, [settings.botTelegramToken]);

  return (
    <div style={{ maxWidth: 560 }}>
      <p>TMDB usa la clave de Integraciones. Los clientes pueden escribir /entretenimiento o /tendencias.</p>
      <TmdbToggle settings={settings} setSettings={setSettings} field="tmdbWhatsappEnabled" label="Entretenimiento TMDB en WhatsApp" />
      <TmdbToggle settings={settings} setSettings={setSettings} field="tmdbTelegramEnabled" label="Entretenimiento TMDB en Telegram" />
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={
              settings.botWhatsappEnabled != null
                ? !!settings.botWhatsappEnabled
                : !!settings.botEnabled
            }
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                botWhatsappEnabled: e.target.checked,
                botEnabled: e.target.checked || !!s.botTelegramEnabled
              }))
            }
          />
        }
        label={tNegocio("pages.settings.botWhatsappEnabled")}
      />
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={
              settings.botTelegramEnabled != null
                ? !!settings.botTelegramEnabled
                : !!settings.botEnabled
            }
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                botTelegramEnabled: e.target.checked,
                botEnabled: !!s.botWhatsappEnabled || e.target.checked
              }))
            }
          />
        }
        label={tNegocio("pages.settings.botTelegramEnabled")}
      />
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={!!settings.botPromoEnabled}
            onChange={(e) => setSettings((s) => ({ ...s, botPromoEnabled: e.target.checked }))}
          />
        }
        label={tNegocio("pages.settings.botPromoEnabled")}
      />
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={settings.botGuidedOnly !== false}
            onChange={(e) => setSettings((s) => ({ ...s, botGuidedOnly: e.target.checked }))}
          />
        }
        label={tNegocio("pages.settings.botGuidedOnly")}
      />
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={settings.botWalletEnabled !== false}
            onChange={(e) => setSettings((s) => ({ ...s, botWalletEnabled: e.target.checked }))}
          />
        }
        label={tNegocio("pages.settings.botWalletEnabled")}
      />
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={settings.botRatingEnabled !== false}
            onChange={(e) => setSettings((s) => ({ ...s, botRatingEnabled: e.target.checked }))}
          />
        }
        label={tNegocio("pages.settings.botRatingEnabled")}
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.settings.botShopName")}
        value={settings.botShopName || ""}
        onChange={(e) => setSettings((s) => ({ ...s, botShopName: e.target.value }))}
        helperText={tNegocio("pages.settings.botShopNameHint")}
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.settings.botTelegramToken")}
        value={tokenDraft}
        onChange={(e) => setTokenDraft(e.target.value)}
        helperText={
          settings.botTelegramUsername
            ? `${tNegocio("pages.settings.botTelegramTokenHintSet")} @${settings.botTelegramUsername}`
            : settings.botTelegramTokenSet
              ? tNegocio("pages.settings.botTelegramTokenHintSet")
              : tNegocio("pages.settings.botTelegramTokenHint")
        }
        placeholder="123456:ABC-DEF..."
        type="password"
        autoComplete="off"
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.settings.botAdminNumber")}
        value={settings.botAdminNumber || ""}
        onChange={(e) => setSettings((s) => ({ ...s, botAdminNumber: e.target.value }))}
        helperText={tNegocio("pages.settings.botAdminNumberHint")}
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.settings.botAdminTelegram")}
        value={settings.botAdminTelegram || ""}
        onChange={(e) => setSettings((s) => ({ ...s, botAdminTelegram: e.target.value }))}
        helperText={tNegocio("pages.settings.botAdminTelegramHint")}
        placeholder="@usuario"
      />
      <TextField
        select
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.settings.botSalesQueue")}
        value={settings.botSalesQueueId || ""}
        onChange={(e) =>
          setSettings((s) => ({
            ...s,
            botSalesQueueId: e.target.value ? Number(e.target.value) : null
          }))
        }
        helperText={
          queues.length
            ? tNegocio("pages.settings.botQueueHint")
            : tNegocio("pages.settings.botQueueEmpty")
        }
      >
        <MenuItem value="">{tNegocio("pages.settings.botQueueNone")}</MenuItem>
        {queues.map((q) => (
          <MenuItem key={q.id} value={q.id}>
            {q.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("pages.settings.botSupportQueue")}
        value={settings.botSupportQueueId || ""}
        onChange={(e) =>
          setSettings((s) => ({
            ...s,
            botSupportQueueId: e.target.value ? Number(e.target.value) : null
          }))
        }
        helperText={
          queues.length
            ? tNegocio("pages.settings.botQueueHint")
            : tNegocio("pages.settings.botQueueEmpty")
        }
      >
        <MenuItem value="">{tNegocio("pages.settings.botQueueNone")}</MenuItem>
        {queues.map((q) => (
          <MenuItem key={q.id} value={q.id}>
            {q.name}
          </MenuItem>
        ))}
      </TextField>
      <NegocioMessageEditor
        label={tNegocio("pages.settings.botWelcome")}
        value={settings.botWelcomeMessage || ""}
        onChange={(v) => setSettings((s) => ({ ...s, botWelcomeMessage: v }))}
        placeholder={tNegocio("pages.settings.botWelcomePlaceholder")}
        showAttach
        mediaUrl={settings.botWelcomeMedia || ""}
        onMedia={async (file) => {
          if (!file) {
            setSettings((s) => ({ ...s, botWelcomeMedia: "" }));
            return;
          }
          try {
            const path = await uploadMedia(file);
            setSettings((s) => ({ ...s, botWelcomeMedia: path }));
          } catch (e) {
            toastError(e);
          }
        }}
        varChips={[
          { key: "nombre_cliente", label: tNegocio("pages.settings.varCustomerName"), sample: "Laura", color: "#00695c", text: "{{nombre_cliente}}" },
          { key: "nombre", label: tNegocio("pages.settings.varFirstName"), sample: "Laura", color: "#0284c7", text: "{{nombre}}" },
        ]}
        preview
        rows={5}
      />
      <div className={classes.captionBlock} style={{ marginTop: 4, marginBottom: 8 }}>
        {tNegocio("pages.settings.botWelcomeHint")}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            onSave({
              botTelegramToken: tokenDraft.trim() ? tokenDraft.trim() : undefined
            })
          }
        >
          {tNegocio("save")}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={testingBot}
          onClick={async () => {
            setTestingBot(true);
            setTestResult(null);
            try {
              // Guardar primero si hay token nuevo en el draft
              if (tokenDraft.trim()) {
                await onSave({ botTelegramToken: tokenDraft.trim() });
              }
              const { data } = await api.post("/negocio/settings/test-telegram-bot");
              setTestResult(data);
              if (data?.ok) {
                toast.success(data.message || tNegocio("pages.settings.botTestOk"));
                if (data.botUsername) {
                  setSettings((s) => ({
                    ...s,
                    botTelegramUsername: data.botUsername,
                    botTelegramTokenSet: true
                  }));
                }
              } else {
                toast.error(data?.error || tNegocio("pages.settings.botTestFail"));
              }
            } catch (e) {
              toastError(e);
              setTestResult({ ok: false, error: e?.response?.data?.error || e.message });
            } finally {
              setTestingBot(false);
            }
          }}
        >
          {testingBot
            ? tNegocio("pages.settings.botTestRunning")
            : tNegocio("pages.settings.botTest")}
        </Button>
      </div>
      {testResult ? (
        <div
          className={classes.captionBlock}
          style={{
            marginTop: 12,
            padding: 10,
            border: `1px solid ${testResult.ok ? "#86efac" : "#fca5a5"}`,
            background: testResult.ok ? "#f0fdf4" : "#fef2f2",
            borderRadius: 8
          }}
        >
          {testResult.ok ? (
            <>
              <div>
                <strong>@{testResult.botUsername || "—"}</strong>
                {testResult.botName ? ` · ${testResult.botName}` : ""}
              </div>
              <div>{testResult.message}</div>
              {testResult.probeError ? <div>{testResult.probeError}</div> : null}
              <div style={{ marginTop: 6 }}>
                {tNegocio("pages.settings.botTestHintApi")}
              </div>
            </>
          ) : (
            <div>{testResult.error || tNegocio("pages.settings.botTestFail")}</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function SettingsPage({ tab: tabProp }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { tab: tabParam } = useParams();
  const tabRaw = tabProp || tabParam || (String(location.pathname).endsWith("/pagos") ? "pagos" : "planes");
  const tab = TAB_ALIASES[tabRaw] || tabRaw;
  const current = TABS.some((t) => t.id === tab) ? tab : "planes";

  useEffect(() => {
    if (TAB_ALIASES[tabRaw] && TAB_ALIASES[tabRaw] !== tabRaw) {
      const next = TAB_ALIASES[tabRaw];
      history.replace(next === "pagos" ? "/negocio/pagos" : `/negocio/configuracion/${next}`);
    }
  }, [tabRaw, history]);
  const [settings, setSettings] = useState({
    currencyLocal: "COP",
    currencySymbol: "$",
    currencyFx: "USD",
    exchangeRate: 1,
    profitPercent: 50,
    binanceCheckoutTtlHours: 72,
    saleMessage: "",
    renewMessage: "",
    expireMessage: "",
    pointsMessage: "",
    updateMessage: "",
    payMessage: "",
    pointsSystemEnabled: true,
    pointsEquivalence: 1000,
    raffleEnabled: false,
    raffleDay: 1,
    raffleWinnerMessage: "",
    raffleAnnounceMessage: "",
    raffleReportNumber: "",
    planCatalog: [],
    termsService: "",
    termsServiceMedia: "",
    termsRaffle: "",
    termsRaffleMedia: "",
    termsPoints: "",
    termsPointsMedia: "",
    botEnabled: false,
    botWhatsappEnabled: false,
    botTelegramEnabled: false,
    botAdminNumber: "",
    botAdminTelegram: "",
    botSalesQueueId: null,
    botSupportQueueId: null,
    botWelcomeMessage: "",
    botWelcomeMedia: "",
    botPromoEnabled: false,
    botShopName: "",
    botTelegramToken: "",
    botTelegramTokenSet: false,
    botTelegramUsername: "",
    botWalletEnabled: true,
    botGuidedOnly: true,
    botRatingEnabled: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/negocio/settings");
        setSettings((s) => ({ ...s, ...data }));
        setNegocioFormat({
          currency: data.currencyLocal || "COP",
          symbol: data.currencySymbol || defaultSymbol(data.currencyLocal || "COP"),
          currencyFx: data.currencyFx || "USD",
          exchangeRate: Number(data.exchangeRate || 1),
          pointsSystemEnabled: data.pointsSystemEnabled !== false && data.pointsSystemEnabled !== 0,
          pointsEquivalence: Number(data.pointsEquivalence || 1000),
          profitPercent: Number(data.profitPercent != null ? data.profitPercent : 50),
        });
      } catch (e) {
        toastError(e);
      }
    })();
  }, []);

  const saveSettings = async (patch = {}, { silent = false } = {}) => {
    const extra = patch && typeof patch === "object" && !patch.nativeEvent ? patch : {};
    try {
      const next = { ...settings, ...extra };
      const { data } = await api.put("/negocio/settings", next);
      setSettings((s) => ({ ...s, ...data }));
      setNegocioFormat({
        currency: data.currencyLocal || next.currencyLocal || "COP",
        symbol: data.currencySymbol || next.currencySymbol || defaultSymbol(next.currencyLocal || "COP"),
        currencyFx: data.currencyFx || next.currencyFx || "USD",
        exchangeRate: Number(data.exchangeRate ?? next.exchangeRate),
        pointsSystemEnabled: data.pointsSystemEnabled !== false && data.pointsSystemEnabled !== 0,
        pointsEquivalence: Number(data.pointsEquivalence || next.pointsEquivalence || 1000),
        profitPercent: Number(data.profitPercent != null ? data.profitPercent : next.profitPercent != null ? next.profitPercent : 50),
      });
      if (!silent) toast.success(tNegocio("toastSaved"));
    } catch (e) {
      toastError(e);
    }
  };

  const meta = TABS.find((t) => t.id === current) || TABS[0];

  return (
    <PageFrame title={meta.title || tNegocio(meta.titleKey)} subtitle={tNegocio(SETTINGS_HINT[current] || "pages.settings.subtitle")}>
      <div className={classes.settingsShell}>
      <div className={classes.settingsTabs}>
        <Tabs
          value={current}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label={tNegocio("pages.settings.subtitle")}
          onChange={(_, value) => history.push(value === "pagos" ? "/negocio/pagos" : `/negocio/configuracion/${value}`)}
        >
          {TABS.map((item) => (
            <Tab key={item.id} value={item.id} label={item.title || tNegocio(item.titleKey)} />
          ))}
        </Tabs>
      </div>
      <div className={classes.settingsPanel}>
      {current === "planes" && (
        <PlansTab settings={settings} setSettings={setSettings} onSave={saveSettings} />
      )}
      {current === "integraciones" && <TmdbSettings settings={settings} setSettings={setSettings} onSave={saveSettings} />}
      {current === "tienda" && <div className={classes.storeSettings}><TmdbToggle settings={settings} setSettings={setSettings} field="tmdbStoreEnabled" label="Mostrar entretenimiento de TMDB en el inicio" /><Button onClick={() => saveSettings({ tmdbStoreEnabled: settings.tmdbStoreEnabled })}>Guardar entretenimiento</Button><StorefrontSettings /></div>}
      {current === "pagos" && (
        <>
          <div className={classes.captionBlock} style={{ marginBottom: 8, fontWeight: 800 }}>
            {tNegocio("pages.settings.tabs.currency")}
          </div>
          <CurrencyTab settings={settings} setSettings={setSettings} onSave={saveSettings} />
          <div className={classes.captionBlock} style={{ margin: "28px 0 8px", fontWeight: 800 }}>
            {tNegocio("pages.settings.paymentMethodsTitle")}
          </div>
          <PaymentsTab />
          <div className={classes.captionBlock} style={{ margin: "28px 0 8px", fontWeight: 800 }}>
            Binance Pay
          </div>
          <BinancePaySettings />
        </>
      )}
      {current === "mensajes" && (
        <>
          <div className={classes.captionBlock} style={{ marginBottom: 8, fontWeight: 800 }}>
            {tNegocio("pages.settings.tabs.messages")}
          </div>
          <MessagesTab settings={settings} setSettings={setSettings} onSaveSettings={saveSettings} />
          <div className={classes.captionBlock} style={{ margin: "28px 0 8px", fontWeight: 800 }}>
            {tNegocio("pages.settings.tabs.reminders")}
          </div>
          <RemindersTab />
        </>
      )}
      {current === "uso" && (
        <ConditionsTab
          settings={settings}
          setSettings={setSettings}
          onSave={saveSettings}
          textKey="termsService"
          mediaKey="termsServiceMedia"
          placeholder={tNegocio("pages.settings.termsPlaceholder")}
          chips={CONDITION_CHIPS.uso}
        />
      )}
      {current === "fidelizacion" && (
        <>
          <div className={classes.captionBlock} style={{ marginBottom: 8, fontWeight: 800 }}>
            {tNegocio("pages.settings.pointsTermsTitle")}
          </div>
          <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
            {tNegocio("pages.settings.pointsHint")}
          </div>
          <ConditionsTab
            settings={settings}
            setSettings={setSettings}
            onSave={saveSettings}
            textKey="termsPoints"
            mediaKey="termsPointsMedia"
            placeholder={tNegocio("pages.settings.pointsPlaceholder")}
            chips={CONDITION_CHIPS.puntos}
          >
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={settings.pointsSystemEnabled !== false}
                  onChange={(e) => setSettings((s) => ({ ...s, pointsSystemEnabled: e.target.checked }))}
                />
              }
              label={tNegocio("pages.settings.pointsSystemEnabled")}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              label={tNegocio("pages.settings.cashbackPercent")}
              value={settings.cashbackPercent ?? 0}
              onChange={(e) => setSettings((s) => ({ ...s, cashbackPercent: e.target.value }))}
              disabled={settings.pointsSystemEnabled === false}
              inputProps={{ min: 0, max: 100, step: "0.5" }}
              style={{ margin: "8px 0 4px", maxWidth: 360 }}
            />
            {settings.pointsSystemEnabled !== false && Number(settings.cashbackPercent) > 0 ? (
              <div className={classes.captionBlock} style={{ margin: "0 0 16px" }}>
                {(() => {
                  const sample = 24000;
                  const rate = Number(settings.exchangeRate) > 0 ? Number(settings.exchangeRate) : 1;
                  const backLocal = (sample * Number(settings.cashbackPercent)) / 100;
                  return tNegocio("pages.settings.cashbackExample")
                    .replace("{amount}", money(sample))
                    .replace(
                      "{back}",
                      `${money(backLocal)} ${String(settings.currencyLocal || "COP").toUpperCase()} (${tNegocio("pages.settings.cashbackAbroad")} ≈ ${defaultSymbol(settings.currencyFx || "USD")}${(backLocal / rate).toFixed(2)} ${settings.currencyFx || "USD"})`
                    );
                })()}
              </div>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              color="primary"
              style={{ margin: "0 0 12px" }}
              onClick={() => setSettings((s) => ({ ...s, termsPoints: cashbackTermsTemplate(s) }))}
            >
              {tNegocio("pages.settings.cashbackTemplate")}
            </Button>
          </ConditionsTab>

          <div className={classes.captionBlock} style={{ margin: "28px 0 8px", fontWeight: 800 }}>
            {tNegocio("pages.settings.tabs.raffle")}
          </div>
          <ConditionsTab
            settings={settings}
            setSettings={setSettings}
            onSave={saveSettings}
            textKey="termsRaffle"
            mediaKey="termsRaffleMedia"
            placeholder={tNegocio("pages.settings.rafflePlaceholder")}
            chips={CONDITION_CHIPS.sorteo}
          >
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!settings.raffleEnabled}
                  onChange={(e) => setSettings((s) => ({ ...s, raffleEnabled: e.target.checked }))}
                />
              }
              label={tNegocio("pages.settings.raffleEnabled")}
            />
            <TextField
              select
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.settings.raffleDay")}
              value={settings.raffleDay == null || settings.raffleDay === "" ? 0 : Number(settings.raffleDay)}
              onChange={(e) => setSettings((s) => ({ ...s, raffleDay: Number(e.target.value) }))}
              helperText={tNegocio("pages.settings.raffleDayHint")}
              disabled={!settings.raffleEnabled}
              style={{ margin: "8px 0 16px", maxWidth: 360 }}
            >
              <MenuItem value={0}>{tNegocio("pages.settings.raffleLastDay")}</MenuItem>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.settings.raffleReportNumber")}
              value={settings.raffleReportNumber || ""}
              onChange={(e) => setSettings((s) => ({ ...s, raffleReportNumber: e.target.value }))}
              helperText={tNegocio("pages.settings.raffleReportNumberHint")}
              disabled={!settings.raffleEnabled}
              style={{ marginBottom: 12 }}
            />
            <NegocioMessageEditor
              label={tNegocio("pages.settings.raffleWinnerMessage")}
              value={settings.raffleWinnerMessage || ""}
              onChange={(v) => setSettings((s) => ({ ...s, raffleWinnerMessage: v }))}
              placeholder={tNegocio("pages.settings.raffleWinnerPlaceholder")}
              varChips={RAFFLE_MSG_CHIPS}
              rows={7}
            />
            <NegocioMessageEditor
              label={tNegocio("pages.settings.raffleAnnounceMessage")}
              value={settings.raffleAnnounceMessage || ""}
              onChange={(v) => setSettings((s) => ({ ...s, raffleAnnounceMessage: v }))}
              placeholder={tNegocio("pages.settings.raffleAnnouncePlaceholder")}
              varChips={RAFFLE_MSG_CHIPS}
              rows={6}
            />
          </ConditionsTab>

          <div className={classes.captionBlock} style={{ margin: "28px 0 8px", fontWeight: 800 }}>
            {tNegocio("pages.growth.title")}
          </div>
          <GrowthSection settings={settings} setSettings={setSettings} onSave={saveSettings} />
        </>
      )}
      {current === "codigos" && <CodeLookupSettings />}
      {current === "bot" && (
        <BotTab settings={settings} setSettings={setSettings} onSave={saveSettings} />
      )}
      {current === "agente-ia" && (
        <AiAgentTab settings={settings} setSettings={setSettings} onSave={saveSettings} />
      )}
      {current === "migrar" && <MigrateAppSheetTab />}
      </div>
      </div>
    </PageFrame>
  );
}
